/**
 * The legacy call bridge.
 *
 * The SPA has 474 call sites across 91 files that POST an `API_Meth` envelope
 * to keyon's `tswebhook.php`, plus ~60 `{data, action}` calls to the retired
 * Yii2 backend. Rewriting them all before the app can run again would mean a
 * long period with nothing working. Instead this module installs an **axios
 * adapter** that recognises those requests, re-expresses each one as a REST
 * call against the new API, and answers with a synthetic response in the exact
 * shape the call site already parses.
 *
 * Why an adapter and not an interceptor: axios 0.21 request interceptors can
 * only mutate a config or reject it — they cannot resolve a request with a
 * synthetic response. An adapter (`config => Promise<AxiosResponse>`) can.
 *
 * The bridge is a migration scaffold, not an architecture. Everything it
 * re-emits is legacy shape; everything underneath it is correct (bearer auth,
 * real status codes, tenancy from the token). `bridgeStats()` is the burn-down:
 * when a component moves to a native `api2/*` client its operations stop
 * appearing, and when the table is empty this file is deleted.
 */

import axios from 'axios';
import { nativeAdapter } from './legacyAdapterRef';
import {
  KEYON_OPERATIONS,
  KEYON_FLAT_OPERATIONS,
  BE_OPERATIONS,
  BE_RETIRED,
  NPI_LOOKUP_OPERATION,
  ICD_LOOKUP_OPERATION,
} from './bridgeMap';
import { legacyError } from './bridgeShapes';
import api, { ApiError } from './client';
import { dispatchFileRequest, isFileRequest } from './fileBridge';

export class BridgeUnmappedOperation extends Error {
  constructor(descriptor, context) {
    super(`No REST mapping for legacy operation "${descriptor}".`);
    this.name = 'BridgeUnmappedOperation';
    this.descriptor = descriptor;
    this.context = context || null;
  }
}

/**
 * A write whose target could not be addressed.
 *
 * Modelled as an `ApiError` so `handle()` re-emits it in whichever legacy
 * failure envelope the operation declares, which is what makes the call site's
 * own error branch fire. The alternative — the empty-result short-circuit the
 * reads use — runs the operation's success shape, and a shape that ignores its
 * argument then reports a write that never happened. `SendNoteGroupNotifications`
 * is the case that forced this: the user was told notifications had been sent
 * while no request had left the browser.
 */
class BridgeUnresolvedTarget extends ApiError {
  constructor(path) {
    super({
      status: 0,
      code: 'bridge_unresolved_target',
      message: 'This action could not be sent: the record it applies to was not identified. '
        + 'Reload the page and try again.',
    });
    this.name = 'BridgeUnresolvedTarget';
    this.path = path;
  }
}

const stats = { calls: 0, byOperation: Object.create(null), unmapped: [] };

export function bridgeStats() {
  return {
    calls: stats.calls,
    byOperation: { ...stats.byOperation },
    unmapped: [...stats.unmapped],
  };
}

let installed = false;
let previousAdapter = null;

export function installLegacyBridge() {
  if (installed) return;
  previousAdapter = axios.defaults.adapter;
  axios.defaults.adapter = bridgeAdapter(nativeAdapter);
  installed = true;
}

export function uninstallLegacyBridge() {
  if (!installed) return;
  axios.defaults.adapter = previousAdapter;
  installed = false;
}

// --------------------------------------------------------------------------

function bridgeAdapter(passthrough) {
  return function adapter(config) {
    const url = String(config.url || '');

    // keyon's file endpoint: a download, not an envelope. Answered with the
    // API's real status (409 scanning, 410 gone, 401 mfa_required, 429) and
    // main's refusal body, because main's download code reads the status
    // (api2/fileBridge.js). Never through handle(), which turns failures
    // into 200s.
    if (isFileRequest(url)) {
      return dispatchFileRequest(config);
    }

    if (url.includes('/tswebhook.php')) {
      return handle(config, () => dispatchKeyon(config));
    }

    if (url.includes('/NPILookup.php')) {
      return handle(config, () => runOperation(NPI_LOOKUP_OPERATION, parseBody(config), config));
    }

    // keyon's ICD-10 search, another single-operation file outside the
    // envelope dispatch. Unbridged, every ICD picker fell through to the
    // retired backend.
    if (url.includes('/icd_calls.php')) {
      return handle(config, () => runOperation(ICD_LOOKUP_OPERATION, parseBody(config), config, 'icd_calls.php'));
    }

    // keyon's chunked video upload endpoint. Intercepted so requests never
    // pass through to the retired backend (CR-05, MV-20). Full REST dispatch
    // lands in P-MEDIA.
    if (url.includes('/video_upload.php')) {
      return handle(config, () => {
        throw new BridgeUnmappedOperation('video_upload.php', { url });
      });
    }

    // The retired Yii2 backend, in full.
    //
    // This used to match only `/online/ajax`, `/online/ajax-file` and
    // `/sessionless/*`. Those are the RPC entry points, but the SPA also calls
    // Yii2 *controller* routes directly — `/contacts/index`, `/session/ping`,
    // `/notes/update` and thirty-odd others — and those fell straight through
    // to a container that no longer exists, surfacing as a proxy ENOTFOUND and
    // a 500 in the browser.
    //
    // The honest condition is "addressed to the retired backend", not a list of
    // its route families, so match the base path itself.
    if (url.includes('index.php') || url.includes('/online/ajax') || url.includes('/sessionless/')) {
      return handle(config, () => dispatchBackend(config));
    }

    return passthrough(config);
  };
}

/** Wrap a handler so it always resolves with an axios-shaped response. */
function handle(config, run) {
  return Promise.resolve()
    .then(run)
    .then((data) => synthetic(config, data, 200))
    .catch((error) => {
      if (error instanceof BridgeUnmappedOperation) {
        // Never fall through to the dead backend — that would surface as a
        // confusing network error instead of a clear migration gap.
        stats.unmapped.push(error.descriptor);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[legacyBridge]', error.message, error.context);
        }
        return Promise.reject(error);
      }

      if (error instanceof ApiError) {
        const style = (error.bridgeErrorStyle) || 'status';
        // Legacy call sites read failure out of a 200 body. Preserve that so
        // existing error handling still fires, and carry the real status for
        // anything that has already been migrated.
        return synthetic(config, legacyError(error, style), 200, { 'x-bridge-status': error.status });
      }

      return Promise.reject(error);
    });
}

function synthetic(config, data, status = 200, headers = {}) {
  return {
    data,
    status,
    statusText: 'OK',
    headers: { 'content-type': 'application/json', 'x-bridged': '1', ...headers },
    config,
    request: null,
  };
}

// --- keyon dispatch --------------------------------------------------------

function dispatchKeyon(config) {
  const body = parseBody(config);

  // Three request shapes reached the same file: an enveloped JSON object, a
  // flat JSON object, and multipart FormData with API_Meth as a field.
  const { envelope, method, payload } = readKeyonCall(body);

  if (!method) {
    throw new BridgeUnmappedOperation('tswebhook.php (no API_Meth)', { body });
  }

  const key = envelope ? `${envelope}.${method}` : method;
  const spec = (envelope && KEYON_OPERATIONS[key]) || KEYON_FLAT_OPERATIONS[method] || KEYON_OPERATIONS[key];

  if (!spec) {
    throw new BridgeUnmappedOperation(key, { envelope, method, payload });
  }

  return runOperation(spec, payload, config, key);
}

function readKeyonCall(body) {
  if (body instanceof FormData) {
    const payload = {};
    body.forEach((value, name) => { payload[name] = value; });
    return {
      envelope: payload.Envelope || null,
      method: payload.API_Meth || null,
      payload,
    };
  }

  if (body && typeof body === 'object') {
    if (body.API_Meth) {
      return { envelope: null, method: body.API_Meth, payload: body };
    }

    // Enveloped: exactly one key whose value carries API_Meth.
    const envelope = Object.keys(body).find(
      (k) => body[k] && typeof body[k] === 'object' && body[k].API_Meth,
    );

    if (envelope) {
      return { envelope, method: body[envelope].API_Meth, payload: body[envelope] };
    }
  }

  return { envelope: null, method: null, payload: body };
}

// --- pacmny-be dispatch ----------------------------------------------------

function dispatchBackend(config) {
  const body = parseBody(config);
  const action = (body && (body.action || body['action-field'])) || null;
  // Everything up to and including the Yii2 front controller is deployment
  // detail — `/pacmny-be/web/index.php/contacts/index` is the `/contacts/index`
  // route. Strip the prefix so the map keys stay readable.
  const route = new URL(String(config.url), window.location.origin).pathname
    .replace(/^.*index\.php/, '')
    .replace(/\/$/, '') || '/';

  const key = `${route}#${action}`;

  // Route first, then the action alone. Several routes share an action name —
  // `/contacts/rename` and `/contacts/set-primary-contact` both send
  // `renameContact` — so the action is not on its own an identifier.
  const spec = BE_OPERATIONS[key] || BE_OPERATIONS[route] || BE_OPERATIONS[`#${action}`];

  if (!spec) {
    const retired = BE_RETIRED[route];

    if (retired) {
      throw new BridgeUnmappedOperation(`${route} (retired)`, { action, reason: retired });
    }

    throw new BridgeUnmappedOperation(key, { action, body });
  }

  // The Yii2 envelope nested the real payload under `data`.
  const payload = body && body.data !== undefined ? body.data : body;

  return runOperation(spec, payload, config, key);
}

// --- Operation execution ---------------------------------------------------

/**
 * An OperationSpec is data, not code:
 *
 *   {
 *     method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
 *     path:   (p) => `/patients/${p.patientid}/allergies`,
 *     query?: (p) => ({ ... }),      // for GET
 *     body?:  (p) => ({ ... }),      // for writes
 *     upload?: true,                 // send as multipart
 *     download?: true,               // expect a blob
 *     shape:  (result, p) => legacyShapedResponse,
 *     errorStyle?: 'status' | 'code' | 'msg' | 'message',
 *     refusals?: { [apiErrorCode]: legacyBody },  // answered, not thrown
 *   }
 */
async function runOperation(spec, payload, config, key) {
  stats.calls += 1;
  if (key) stats.byOperation[key] = (stats.byOperation[key] || 0) + 1;

  const p = payload || {};

  // A locally-resolved operation.
  //
  // A few legacy calls are not one REST request: entering and leaving a
  // patient's chart select the chart in the SPA as well as asking the API to
  // open or close it, and one care-team call is several requests. They still
  // arrive here, because the call sites are unmigrated, so the spec runs a
  // function instead of building a path.
  if (typeof spec.local === 'function') {
    // Through the same error-style stamping as a path-driven operation: a local
    // spec can still fail with an `ApiError` (it may issue its own requests),
    // and the call site reads that failure out of the envelope it was written
    // for, not out of the default one.
    try {
      return await spec.local(p, config);
    } catch (error) {
      if (error instanceof ApiError && spec.errorStyle) {
        error.bridgeErrorStyle = spec.errorStyle;
      }

      throw error;
    }
  }

  const path = typeof spec.path === 'function' ? spec.path(p) : spec.path;

  // An unresolved segment means no record is in view — not a failure.
  //
  // The path is built from a payload the call site assembles, and a component
  // that has not been migrated still reads its ids from the retired
  // `getAllData` blob. Before a chart is selected those are null, so the path
  // comes out as `/patients/null/allergies`.
  //
  // This used to throw. That was wrong twice over: the legacy catch blocks
  // branch on `axios.isAxiosError` and so funnel anything else into
  // "we hit an unexpected snag, please contact your system administrator",
  // which fired on every page load; and the condition is not an error at all.
  // "No patient selected" is a normal state for a clinician who has not opened
  // a chart, and the correct answer to "allergies for nobody" is "none", not an
  // exception.
  //
  // Still logged, once per operation, so the burn-down list stays visible.
  //
  // A **write** is the opposite case and must not take this branch. "Save this
  // note's notifications for nobody" is not a normal state, and running the
  // operation's success shape over an empty result reports a write that never
  // reached the API — the shapes here are mostly constant functions, so they
  // fabricate `{message: 'NotificationsSent'}` regardless of what happened. A
  // write with no target fails, loudly, in the envelope the call site parses.
  if (hasUnresolvedSegment(path)) {
    reportUnresolvedPath(path, key, p);

    if (!isRead(spec)) {
      const unresolved = new BridgeUnresolvedTarget(path);

      if (spec.errorStyle) unresolved.bridgeErrorStyle = spec.errorStyle;

      throw unresolved;
    }

    const empty = emptyResultFor(spec, p);

    return spec.shape ? spec.shape(empty, p) : empty;
  }

  let result;

  try {
    if (spec.download) {
      result = await api.download(path, spec.query ? spec.query(p) : {});
    } else if (spec.upload) {
      result = await api.upload(path, buildFormData(spec.body ? spec.body(p) : p));
    } else {
      switch ((spec.method || 'GET').toUpperCase()) {
        case 'POST':   result = await api.post(path, spec.body ? spec.body(p) : p); break;
        case 'PUT':    result = await api.put(path, spec.body ? spec.body(p) : p); break;
        case 'PATCH':  result = await api.patch(path, spec.body ? spec.body(p) : p); break;
        case 'DELETE': result = await api.delete(path); break;
        default:
          result = isPaginated(spec, p)
            ? await api.list(path, spec.query ? spec.query(p) : {})
            : await api.get(path, spec.query ? spec.query(p) : {});
      }
    }
  } catch (error) {
    // A refusal the legacy backend answered with a body of its own, which the
    // call site branches on (`#ccda`: a duplicate was {message: 'cdaExists'}),
    // is answered with that body; every other failure keeps its error style.
    if (error instanceof ApiError && spec.refusals
      && Object.prototype.hasOwnProperty.call(spec.refusals, error.code)) {
      return spec.refusals[error.code];
    }
    if (error instanceof ApiError && spec.errorStyle) {
      error.bridgeErrorStyle = spec.errorStyle;
    }
    throw error;
  }

  return spec.shape ? spec.shape(result, p) : result;
}

/**
 * Whether a path still carries a placeholder the caller never filled in.
 *
 * `NaN` is in the set because the ids reaching a path go through `Number()`,
 * and the values that fail it are not hypothetical: a gen-2 order number is
 * `'N' + orderId`, so `/orders/NaN/signatures` was a request the bridge
 * actually sent and the API answered 404. Anything that did not resolve to a
 * usable segment is "no record in view", however it failed to resolve.
 */
function hasUnresolvedSegment(path) {
  return /\/(null|undefined|NaN)(\/|$)/.test(path);
}

/** Whether an operation only reads — the one case an unresolved path is normal. */
function isRead(spec) {
  return spec.download === true
    || (!spec.upload && (spec.method || 'GET').toUpperCase() === 'GET');
}

/**
 * Whether this call reads a paginated collection.
 *
 * Resolvable per call, not just per operation: a few legacy operations dispatch
 * on an `action` string and hit different endpoints for it, and those endpoints
 * do not agree about pagination. `NursyInfo.GetAccountNurse` is the case that
 * forced this — `action=GetNursesAssigned` reads `/patients/{id}/care-team`,
 * which answers with a single slot-keyed object, while every other action reads
 * the paginated `/users`. Treating the object as a page put it in `items`, so
 * the caller got an object where it expected an array and rendered nothing.
 */
function isPaginated(spec, payload) {
  return typeof spec.paginated === 'function'
    ? Boolean(spec.paginated(payload || {}))
    : Boolean(spec.paginated);
}

/**
 * What an operation returns when there is nothing to fetch.
 *
 * Matches the shape the operation would have produced on success, so a caller's
 * own `.map`/`.length` handling works unchanged: a page for a paginated read, a
 * list for a collection, null for a single record.
 */
function emptyResultFor(spec, payload) {
  if (isPaginated(spec, payload)) return { items: [], meta: null, links: null };
  if ((spec.method || 'GET').toUpperCase() === 'GET') return spec.collection === false ? null : [];
  return null;
}

/** Log an unmigrated call site once, rather than on every render. */
const reportedUnresolved = new Set();

function reportUnresolvedPath(path, key, payload) {
  const descriptor = key || path;

  if (reportedUnresolved.has(descriptor)) return;
  reportedUnresolved.add(descriptor);

  // eslint-disable-next-line no-console
  console.warn(
    `[legacyBridge] "${descriptor}" was called with no record in view — resolved to "${path}", `
    + 'so it returned empty instead of calling the API. The call site still reads its ids from the '
    + 'retired getAllData blob; migrate it to a native api2 client.',
    { payload },
  );
}

/**
 * A multipart body, with arrays sent as arrays.
 *
 * PHP builds an array parameter from repeated `name[]` parts; a single
 * `form.append('participant_ids', [12, 13])` stringifies to `"12,13"` and the
 * API's `participant_ids => array` rule then fails, which is why every new
 * message thread and every attachment upload answered 422. An array value here
 * therefore appends one `name[]` part per element — and an empty array appends
 * nothing, because "no participants" and "one participant spelt ''" are
 * different claims.
 */
function buildFormData(source) {
  if (source instanceof FormData) return source;

  const form = new FormData();

  Object.entries(source || {}).forEach(([name, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null) form.append(`${name}[]`, entry);
      });

      return;
    }

    form.append(name, value);
  });

  return form;
}

function parseBody(config) {
  const { data } = config;

  if (data instanceof FormData) return data;
  if (typeof data === 'string') {
    try { return JSON.parse(data); } catch (e) { return data; }
  }
  return data;
}

export default { installLegacyBridge, uninstallLegacyBridge, bridgeStats };
